import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, BadgeCheck, CalendarClock, CreditCard, Users, ShieldCheck, Ban, HandHeart, Flag, MapPin, Film, ShoppingBag, Coffee, PartyPopper, Landmark, MessageCircle, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanionCard } from "@/components/CompanionCard";
import { DatePicker } from "@/components/DatePicker";
import { EmergencyButton } from "@/components/EmergencyButton";
import { api, getActivities, todayIST } from "@/lib/api";
import { useLang, actLabel } from "@/lib/i18n";

const ICONS = { movie: Film, shopping: ShoppingBag, cafe: Coffee, event: PartyPopper, sightseeing: Landmark, conversation: MessageCircle };
const HOURS = Array.from({ length: 14 }, (_, i) => `${String(i + 9).padStart(2, "0")}:00`);

const SearchCard = ({ acts }) => {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [f, setF] = useState({ city: "Dumka", date: "", time: "any", activity: "any" });
  const go = () => {
    const p = new URLSearchParams();
    if (f.city) p.set("city", f.city);
    if (f.date) p.set("date", f.date);
    if (f.date && f.time !== "any") p.set("time", f.time);
    if (f.activity !== "any") p.set("activity", f.activity);
    nav(`/companions?${p}`);
  };
  return (
    <div className="card-surface p-5 sm:p-6 shadow-2xl shadow-emerald-900/10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end" data-testid="home-search-card">
      <label className="text-xs font-semibold text-slate-500 space-y-1.5">{t("search_city")}
        <Input data-testid="search-city-input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
      </label>
      <div className="text-xs font-semibold text-slate-500 space-y-1.5">{t("search_date")}
        <DatePicker testId="search-date-input" min={todayIST()} clearable value={f.date} onChange={(v) => setF({ ...f, date: v })} />
      </div>
      <div className="text-xs font-semibold text-slate-500 space-y-1.5">{t("search_time")}
        <Select value={f.time} onValueChange={(v) => setF({ ...f, time: v })}>
          <SelectTrigger data-testid="search-time-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("any_time")}</SelectItem>{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="text-xs font-semibold text-slate-500 space-y-1.5">{t("search_activity")}
        <Select value={f.activity} onValueChange={(v) => setF({ ...f, activity: v })}>
          <SelectTrigger data-testid="search-activity-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="any">{t("any_activity")}</SelectItem>{acts.map((a) => <SelectItem key={a.slug} value={a.slug}>{actLabel(a, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button data-testid="search-submit-btn" className="h-10 rounded-full" onClick={go}><Search size={16} className="mr-1.5" />{t("search_btn")}</Button>
    </div>
  );
};

const Hero = ({ acts }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden grain">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#ECFDF5,transparent_60%)]" />
      <div className="container-x relative pt-12 pb-10 lg:pt-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-7 fade-up">
          <p className="eyebrow flex items-center gap-2"><MapPin size={14} />{t("hero_eyebrow")}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-slate-900" data-testid="hero-title">{t("hero_title")}</h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">{t("hero_sub")}</p>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="rounded-full h-12 px-6 bg-[#0D5C46] hover:bg-[#0A4837]" data-testid="filter-female-btn"><Link to="/companions?gender=female">{t("find_female")}</Link></Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-6 border-slate-900" data-testid="filter-male-btn"><Link to="/companions?gender=male">{t("find_male")}</Link></Button>
          </div>
          <div className="flex flex-wrap gap-5 text-sm text-slate-600 pt-2">
            <span className="flex items-center gap-1.5"><BadgeCheck size={16} className="text-[#0D5C46]" />ID-verified</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#0D5C46]" />18+ only</span>
            <span className="flex items-center gap-1.5"><HandHeart size={16} className="text-[#0D5C46]" />Strictly platonic</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative h-[380px] sm:h-[460px] fade-up" style={{ animationDelay: ".15s" }}>
          <img src="/demo/hero.jpg" alt="Friends at a cafe" className="absolute right-0 top-0 w-[78%] h-[78%] object-cover rounded-[2rem] shadow-2xl" />
          <img src="/demo/talk.jpg" alt="Conversation outdoors" className="absolute left-0 bottom-0 w-[48%] h-[55%] object-cover rounded-[1.5rem] border-[6px] border-[#FAF9F6] shadow-xl" />
          <div className="absolute right-4 bottom-6 card-surface px-4 py-3 shadow-lg">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">From</p>
            <p className="font-display text-3xl font-bold text-[#0D5C46]">₹499<span className="text-sm font-sans text-slate-500">/hr</span></p>
          </div>
        </div>
      </div>
      <div className="container-x relative pb-14"><SearchCard acts={acts} /></div>
    </section>
  );
};

const ActivitiesStrip = ({ acts }) => {
  const { t, lang } = useLang();
  return (
    <section className="container-x py-16">
      <p className="eyebrow">{t("activities")}</p>
      <h2 className="text-3xl sm:text-4xl font-semibold mt-2 mb-8">{t("activities_title")}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {acts.map((a) => {
          const I = ICONS[a.slug] || Users;
          return (
            <Link key={a.slug} to={`/companions?activity=${a.slug}`} data-testid={`activity-tile-${a.slug}`} className="card-surface p-5 flex flex-col gap-4 hover:border-[#0D5C46] hover:-translate-y-0.5 transition-[transform,border-color]">
              <span className="w-11 h-11 rounded-xl bg-emerald-50 text-[#0D5C46] grid place-items-center"><I size={20} /></span>
              <span className="text-sm font-semibold leading-snug">{actLabel(a, lang)}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useLang();
  const steps = [[BadgeCheck, "how_1"], [CalendarClock, "how_2"], [CreditCard, "how_3"], [Users, "how_4"]];
  return (
    <section id="how" className="bg-[#0D5C46] text-emerald-50 py-20" data-testid="how-it-works-section">
      <div className="container-x">
        <p className="eyebrow !text-emerald-300">{t("nav_how")}</p>
        <h2 className="text-3xl sm:text-4xl font-semibold mt-2 mb-12 text-white">{t("how_title")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map(([I, k], i) => (
            <div key={k} className="space-y-3 border-t border-emerald-700 pt-6">
              <span className="font-mono text-xs text-emerald-300">0{i + 1}</span>
              <I size={26} />
              <h3 className="text-2xl font-semibold text-white">{t(`${k}t`)}</h3>
              <p className="text-sm text-emerald-100/80 leading-relaxed">{t(`${k}d`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SafetySupport = () => {
  const { t } = useLang();
  const rules = [[Ban, "safety_1"], [MapPin, "safety_2"], [HandHeart, "safety_3"], [ShieldCheck, "safety_4"], [Flag, "safety_5"]];
  return (
    <section className="container-x py-20 grid lg:grid-cols-12 gap-8" data-testid="safety-section">
      <div className="lg:col-span-7 card-surface p-8">
        <p className="eyebrow">{t("nav_safety")}</p>
        <h2 className="text-3xl sm:text-4xl font-semibold mt-2">{t("safety_title")}</h2>
        <p className="text-slate-600 mt-3 mb-6">{t("safety_sub")}</p>
        <ul className="space-y-3">
          {rules.map(([I, k]) => <li key={k} className="flex items-center gap-3 text-sm font-medium"><I size={18} className="text-[#0D5C46] shrink-0" />{t(k)}</li>)}
        </ul>
        <Link to="/guidelines" className="inline-block mt-6 text-sm font-semibold text-[#0D5C46]" data-testid="home-guidelines-link">{t("guidelines")} →</Link>
      </div>
      <div className="lg:col-span-5 rounded-2xl bg-[#0F172A] text-slate-200 p-8 flex flex-col gap-5" data-testid="support-section">
        <LifeBuoy className="text-emerald-400" size={30} />
        <h2 className="text-3xl font-semibold text-white">{t("support_title")}</h2>
        <p className="text-sm text-slate-400">{t("support_desc")}</p>
        <div className="flex flex-wrap gap-3 mt-auto">
          <Button asChild className="rounded-full bg-white text-slate-900 hover:bg-emerald-50" data-testid="home-contact-support-btn"><Link to="/support">{t("contact_support")}</Link></Button>
          <Button asChild variant="outline" className="rounded-full bg-transparent text-white border-slate-600 hover:bg-slate-800 hover:text-white" data-testid="home-assistance-btn"><Link to="/support?type=assistance">{t("request_help")}</Link></Button>
          <EmergencyButton />
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const [acts, setActs] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    getActivities().then(setActs).catch(() => {});
    api.get("/companions").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <Hero acts={acts} />
      <ActivitiesStrip acts={acts} />
      <section className="container-x pb-20">
        <div className="flex items-end justify-between mb-8">
          <div><p className="eyebrow">Dumka & nearby</p><h2 className="text-3xl sm:text-4xl font-semibold mt-2">{t("featured")}</h2></div>
          <Link to="/companions" className="text-sm font-semibold text-[#0D5C46]" data-testid="home-view-all-link">{t("view_all")} →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{featured.map((c) => <CompanionCard key={c.id} c={c} acts={acts} />)}</div>
      </section>
      <HowItWorks />
      <SafetySupport />
      <section className="container-x pb-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-amber-50 border border-amber-200 p-8 sm:p-12 grid md:grid-cols-2 gap-6 items-center">
          <div className="space-y-4">
            <p className="eyebrow !text-amber-700">{t("nav_become")}</p>
            <h2 className="text-3xl sm:text-4xl font-semibold">{t("become_title")}</h2>
            <p className="text-slate-600">{t("become_desc")}</p>
            <Button asChild className="rounded-full h-12 px-6 bg-[#D97706] hover:bg-[#B45309]" data-testid="home-apply-btn"><Link to="/become-companion">{t("apply_now")}</Link></Button>
          </div>
          <img src="/demo/event.jpg" alt="Community event" className="rounded-2xl h-64 w-full object-cover" />
        </div>
      </section>
    </div>
  );
}
